import React from 'react';
import { useNavigate } from 'react-router-dom';
import { X, CheckCircle, Circle, Sparkles, ChevronRight } from 'lucide-react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebaseInstance';
import { getAppId } from '../firebaseConfig';

const appId = getAppId();

// Onboarding redesign: a single ordered checklist that includes the AI
// training step. Previous welcome modal had 5 items (avatar / username /
// bio / links / spotlight) and silently omitted Q&A — the most important
// activation step. New users completed the visible checklist, never
// trained the AI, and shipped a profile whose chat answered "I don't
// know" to everything. See QA report 2026-04-24, finding #3.
//
// Five steps, in order of value: username, voice, AI training (featured),
// links, polish. Each step is clickable and routes to the relevant
// editor tab; the modal closes on click so the user lands directly on
// the field they need to fill.

const WelcomeModal = ({ isOpen, onClose, profileData, navigate, user, setProfileData }) => {
  if (!isOpen) return null;

  const qnaPairsCompleted = Array.isArray(profileData.qna)
    ? profileData.qna.filter(q => q && q.answer && q.answer.trim().length > 0).length
    : 0;
  const aiTrained = qnaPairsCompleted >= 3;

  const steps = [
    {
      key: 'username',
      label: 'Claim your username',
      hint: 'This is your URL: perai.me/yourname.',
      done: !!profileData.username,
      tab: 'profile',
      featured: false,
    },
    {
      key: 'identity',
      label: 'Tell your AI who you are',
      hint: 'Add a name, a one-liner, and a short bio so your AI has context.',
      done: !!profileData.name && !!profileData.bio && profileData.bio.trim().length > 0,
      tab: 'profile',
      featured: false,
    },
    {
      key: 'brain',
      label: 'Train your AI to answer questions',
      hint: aiTrained
        ? `${qnaPairsCompleted} Q&A pairs. Your AI knows what you sound like.`
        : `Add at least 3 Q&A pairs. ${qnaPairsCompleted}/3 so far. Without this, your AI says "I don't know" to almost everything.`,
      done: aiTrained,
      tab: 'brain',
      featured: true,
    },
    {
      key: 'links',
      label: 'Add your links',
      hint: 'Instagram, website, calendar, anything visitors should reach.',
      done: profileData.links?.length > 0,
      tab: 'profile',
      featured: false,
    },
    {
      key: 'polish',
      label: 'Pick a theme + photo',
      hint: 'Polish the look. Default initials avatar works fine until you upload one.',
      done: !!profileData.avatar && !!profileData.theme,
      tab: 'settings',
      featured: false,
    },
  ];

  const completed = steps.filter(s => s.done).length;
  const total = steps.length;
  const percent = Math.round((completed / total) * 100);

  // Mark welcome as seen, don't bring it back. Persists to Firestore so
  // the dismissal survives across devices; falls back to localStorage.
  const persistDismiss = async () => {
    if (user) {
      try {
        const userRef = doc(db, 'artifacts', appId, 'users', user.uid);
        await updateDoc(userRef, { hasSeenWelcome: true });
        if (setProfileData) {
          setProfileData(prev => ({ ...prev, hasSeenWelcome: true }));
        }
      } catch (error) {
        console.error('Error saving welcome preference:', error);
        localStorage.setItem(`hasSeenWelcome_${user.uid}`, 'true');
        if (setProfileData) {
          setProfileData(prev => ({ ...prev, hasSeenWelcome: true }));
        }
      }
    } else {
      localStorage.setItem('hasSeenWelcome_guest', 'true');
    }
  };

  const handleDismiss = async () => {
    onClose();
    await persistDismiss();
  };

  const goToStep = async (step) => {
    onClose();
    await persistDismiss();
    if (navigate) {
      navigate(`/editor?tab=${step.tab}`);
    }
  };

  const firstIncomplete = steps.find(s => !s.done);

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={handleDismiss}
    >
      <div
        className="bg-white rounded-2xl p-6 sm:p-7 max-w-md w-full shadow-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-1">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Welcome to PerAI</h2>
            <p className="text-gray-600 text-sm mt-1">Five steps to a bio that actually answers questions.</p>
          </div>
          <button
            onClick={handleDismiss}
            className="text-gray-400 hover:text-gray-600 -mr-1 -mt-1"
            aria-label="Close"
          >
            <X size={22} />
          </button>
        </div>

        <div className="bg-gray-50 rounded-xl p-3 my-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-gray-700 uppercase tracking-wide">Progress</span>
            <span className="text-xs text-gray-500 font-mono">{completed}/{total}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-1.5">
            <div
              className="bg-violet-500 h-1.5 rounded-full transition-all duration-500"
              style={{ width: `${percent}%` }}
            />
          </div>
        </div>

        <ul className="space-y-2 mb-5">
          {steps.map(step => {
            const Icon = step.done ? CheckCircle : Circle;
            const iconColor = step.done ? 'text-green-500' : (step.featured ? 'text-violet-500' : 'text-gray-300');
            return (
              <li key={step.key}>
                <button
                  type="button"
                  onClick={() => goToStep(step)}
                  className={`w-full text-left p-3 rounded-xl border transition-all flex items-start gap-3 group ${
                    step.featured && !step.done
                      ? 'border-violet-200 bg-violet-50 hover:bg-violet-100'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <Icon size={20} className={`shrink-0 mt-0.5 ${iconColor}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className={`font-semibold text-sm ${step.done ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                        {step.label}
                      </span>
                      {step.featured && !step.done && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wider text-violet-600">
                          <Sparkles size={10} />
                          The unlock
                        </span>
                      )}
                    </div>
                    {step.hint && (
                      <p className={`text-xs mt-1 leading-snug ${step.done ? 'text-gray-400' : 'text-gray-600'}`}>
                        {step.hint}
                      </p>
                    )}
                  </div>
                  {!step.done && (
                    <ChevronRight size={18} className="shrink-0 text-gray-400 group-hover:text-gray-700 mt-0.5" />
                  )}
                </button>
              </li>
            );
          })}
        </ul>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleDismiss}
            className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Skip for now
          </button>
          <button
            onClick={() => firstIncomplete ? goToStep(firstIncomplete) : handleDismiss()}
            className="flex-1 px-4 py-2.5 bg-black text-white rounded-xl text-sm font-semibold hover:bg-gray-800 transition-colors flex items-center justify-center gap-1.5"
          >
            {firstIncomplete ? <>Start with: {firstIncomplete.label.split(' ').slice(0, 2).join(' ')}</> : 'Looks great'}
            {firstIncomplete && <ChevronRight size={16} />}
          </button>
        </div>
      </div>
    </div>
  );
};

export const WelcomeModalWrapper = ({ isOpen, onClose, profileData, user, setProfileData }) => {
  const navigate = useNavigate();
  return (
    <WelcomeModal
      isOpen={isOpen}
      onClose={onClose}
      profileData={profileData}
      user={user}
      navigate={navigate}
      setProfileData={setProfileData}
    />
  );
};
